import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
export function SaleHero() {
  const {
    t,
    language
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const priceLabel = language === 'ru' ? 'Специальная цена' : language === 'ua' ? 'Спеціальна ціна' : 'Special Price';
  const monthLabel = language === 'ru' ? '/ мес' : language === 'ua' ? '/ міс' : '/ mo';
  return <>
      <section className="relative w-full overflow-hidden px-4 pt-12 pb-16 flex items-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-221" data-mp="5gn3ylkwgea6">
        <div className="absolute inset-0 overflow-hidden opacity-20" data-id="element-222" data-mp="2q6kreeap0j89">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl" style={{
          background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
        }} data-id="element-223" data-mp="2q8fbjqe547ba" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16" data-id="element-224" data-mp="3jcbhlwuyw8ir">
          {/* Left side - Content */}
          <div className="w-full md:flex-1 text-center md:text-left flex flex-col items-center md:items-start" data-id="element-225" data-mp="3t7vf2np6jf9m">
            {/* Heading */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="mb-6 text-4xl leading-tight tracking-tight md:text-5xl lg:text-6xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-226" data-mp="b0yucy6mh8p2">
              {t('sale.hero.title')}{' '}
              <span className="inline-block px-4 py-1 sm:px-5 sm:py-2 mt-2 sm:mt-0 rounded-2xl sm:rounded-3xl shadow-md whitespace-nowrap" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-227" data-mp="3inhgvcdpu0hk">
                {t('sale.hero.titleHighlight')}
              </span>
            </motion.h1>

            {/* Video - Mobile only */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="block md:hidden mb-8 w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl border border-[var(--border)]" data-id="element-228" data-mp="57jtlz8a38nh">
              <video src="https://cdn.magicpatterns.com/uploads/3rPLLyoyg624hq2VnbLQCY/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-229" data-mp="2kzz65w0huoax" />
            </motion.div>

            {/* Combined Pricing & Subtitle Box */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="mb-8 p-5 sm:p-6 rounded-2xl border w-full max-w-md shadow-sm" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-230" data-mp="1zojqd8emzwzm">
              {/* Changed from flex-col items-center md:items-start text-center md:text-left to flex flex-col items-center text-center */}
              <div className="flex flex-col items-center text-center" data-id="element-231" data-mp="2uviq2c52kgcz">
                <div className="flex items-center gap-3 mb-3" data-id="element-232" data-mp="2uu7qypmkjws7">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wider" style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} data-id="element-233" data-mp="2zobwd4g6rwr6">
                    🔥 {priceLabel}
                  </span>
                </div>
                <div className="text-3xl sm:text-4xl font-bold mb-3 flex items-baseline gap-1" style={{
                color: 'var(--text-primary)'
              }} data-id="element-234" data-mp="3ij91cieypi04">
                  €15
                  <span className="text-lg sm:text-xl font-medium text-gray-500" data-id="element-235" data-mp="1t5neolryjwgf">
                    {monthLabel}
                  </span>
                </div>
                <p className="text-sm sm:text-base font-medium leading-relaxed" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-236" data-mp="1por8b96tdhkg">
                  {t('sale.hero.subtitle')}
                </p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="w-full max-w-md" data-id="element-237" data-mp="1up5ioxvw13ln">
              <motion.button onClick={() => setIsPopupOpen(true)} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group flex items-center justify-center gap-2 rounded-2xl px-8 py-4 font-bold text-lg transition-all w-full shadow-md" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-238" data-mp="1bwukhhc6httv">
                {t('hero.cta')}
                <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-239" data-mp="2t3423t284qsp" />
              </motion.button>
            </motion.div>
          </div>

          {/* Right side - Video (Desktop only) */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="hidden md:flex md:flex-1 items-center justify-center h-full" data-id="element-240" data-mp="25avvd5yynw2h">
            <div className="relative w-full max-w-2xl mx-auto rounded-3xl overflow-hidden shadow-2xl border border-[var(--border)]" data-id="element-241" data-mp="3pr0nrcciqcas">
              <video src="https://cdn.magicpatterns.com/uploads/3rPLLyoyg624hq2VnbLQCY/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-242" data-mp="3oi5b6661fkl" />
            </div>
          </motion.div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-243" data-mp="3h46e9szx7oot" />
    </>;
}